import React, { useState, useEffect } from "react";
import MembersTable from "./MembersTable.jsx";
import AnalyticsTable from "./AnalyticsTable.jsx";
import AggregatesPanel from "./AggregatesPanel.jsx";
import DivisionCheckIn from "./DivisionCheckIn.jsx";
import DistrictCheckIn from "./DistrictCheckIn.jsx";
import StateCheckIn from "./StateCheckIn.jsx";
import SectorCheckIn from "./SectorCheckIn.jsx";
import CompletionProgress from "./CompletionProgress.jsx";

export default // Main Dashboard Component
function Dashboard({ token, setToken }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("drilldown");

  useEffect(() => {
    fetch("https://smartcoreapi.ssfkerala.org/auth/me.php", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(setUser)
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  if (!user) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-red-600">Error fetching user data</div>
    </div>
  );

  const logout = () => {
    setToken(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-center sm:text-left">
              <h1 className="text-xl sm:text-2xl font-bold text-white">Smart Core</h1>
              <p className="text-xs sm:text-sm text-blue-100">Future Summit</p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <span className="text-sm sm:text-base text-white text-center sm:text-right">Welcome {user.orgname}</span>
              <button
                onClick={logout}
                className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-medium transition backdrop-blur-sm w-full sm:w-auto"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {user.role_id === 4 ? (
          <MembersTable token={token} user={user} />
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-sm p-2 mb-6 flex gap-2 overflow-x-auto">
              <button
                onClick={() => setView("drilldown")}
                className={`flex-1 min-w-fit px-4 py-2 rounded-lg font-medium transition ${
                  view === "drilldown"
                    ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                Drill Down
              </button>
              <button
                onClick={() => setView("entities")}
                className={`flex-1 min-w-fit px-4 py-2 rounded-lg font-medium transition ${
                  view === "entities"
                    ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                Sub-Entities
              </button>
              <button
                onClick={() => setView("members")}
                className={`flex-1 min-w-fit px-4 py-2 rounded-lg font-medium transition ${
                  view === "members"
                    ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                All Members
              </button>
              {(user.role_id === 1 || user.role_id === 2 || user.role_id === 3) && (
                <button
                  onClick={() => setView("completions")}
                  className={`flex-1 min-w-fit px-4 py-2 rounded-lg font-medium transition ${
                    view === "completions"
                      ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Completion Progress
                </button>
              )}
              {(user.role_id === 1 || user.role_id === 2 || user.role_id === 3 || user.role_id === 4) && (
                <button
                  onClick={() => setView("checkin")}
                  className={`flex-1 min-w-fit px-4 py-2 rounded-lg font-medium transition ${
                    view === "checkin"
                      ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Check-In
                </button>
              )}
            </div>

            <div>
              {view === "drilldown" && <AnalyticsTable token={token} />}
              {view === "entities" && <AggregatesPanel token={token} user={user} />}
              {view === "members" && <MembersTable token={token} user={user} />}
              {view === "completions" && (user.role_id === 1 || user.role_id === 2 || user.role_id === 3) && (
                <CompletionProgress token={token} user={user} />
              )}
              {view === "checkin" && user.role_id === 3 && <DivisionCheckIn token={token} user={user} />}
              {view === "checkin" && user.role_id === 2 && <DistrictCheckIn token={token} user={user} />}
              {view === "checkin" && user.role_id === 1 && <StateCheckIn token={token} user={user} />}
              {view === "checkin" && user.role_id === 4 && <SectorCheckIn token={token} user={user} sectorId={user.id} />}
            </div>
          </>
        )}

        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">Powered by SSF Kerala</p>
        </div>
      </div>
    </div>
  );
}